import React, { useEffect, useState } from 'react';

// Components.
import CustomForm from './form';

// Services.
import getFormErrors from '../services/getFormErrors';

// Styles.
import '../scss/newsletters.scss';

const Newsletters = () => {
  // State variable to keep track of error messages.
  const [errors, setErrors] = useState([]);

  // State variables for form fields.
  const [formValues, setFormValues] = useState({
    consent: false,
    email: '',
    first_name: '',
    last_name: '',
    zip_code: '',
    phone: '',
  });

  // State variable to keep track of loaded newsletter group data.
  const [groups, setGroups] = useState([]);

  // State variable to keep track of config loading status.
  const [groupsLoading, setGroupsLoading] = useState(true);

  // State variable for form validation status.
  const [isValid, setIsValid] = useState(true);

  // State variable for whether the form submission request is being sent.
  const [loading, setLoading] = useState(false);

  // State variable to keep track of the selected lists.
  const [selectedLists, setSelectedLists] = useState([]);

  // State variable for subscription status.
  const [status, setStatus] = useState('');

  /**
   * A helper function to perform form validation.
   * @returns {boolean} True if the form is valid, false if it is not.
   */
  const validateForm = () => {
    const newErrors = getFormErrors(formValues, selectedLists);
    setErrors(newErrors);
    if (0 === newErrors.length && false === isValid) {
      setIsValid(true);
    } else if (0 < newErrors.length && true === isValid) {
      setIsValid(false);
    }

    return 0 === newErrors.length;
  };

  /**
   * Update the given form input with the given value based on key.
   * @param {string} key - The state key to update.
   * @param {string} value - The value to set.
   */
  const onInputChange = (key, value) => {
    setFormValues({
      ...formValues,
      [key]: value,
    });
  };

  /**
   * Given a list ID and a type, toggles subscription to that list on or off.
   * @param {string} listId - The list ID to subscribe or unsubscribe to.
   * @param {string} type - The type of subscription - either `email` or `sms`.
   */
  const onListSelect = (listId, type) => {
    const listKey = `${listId}-${type}`;
    const index = selectedLists.indexOf(listKey);
    if (- 1 !== index) {
      setSelectedLists([
        ...selectedLists.slice(0, index),
        ...selectedLists.slice(index + 1),
      ]);
    } else {
      setSelectedLists([
        ...selectedLists,
        listKey,
      ]);
    }
  };

  /**
   * A handler for form submission.
   * @param {string} token - reCAPTCHA token.
   */
  const onFormSubmit = (token = '') => {
    // If the form is invalid, bail out.
    if (! validateForm()) {
      return;
    }

    // Set loading flag to indicate that the submit is happening.
    setLoading(true);

    // Send the data to the custom endpoint.
    (async () => {
      try {
        const rawResponse = await fetch(
          '/wp-json/kauffman/v1/newsletter/subscribe',
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ...formValues,
              selectedLists,
              token,
            }),
          }
        );
        const {
          data: {
            status: responseStatus = 200,
          } = {},
          message = '',
        } = await rawResponse.json();
        if (200 === responseStatus) {
          setStatus('subscribed');
        } else if ('' !== message) {
          setErrors([message]);
        }
      } catch ({ message }) {
        setErrors([message]);
      } finally {
        setLoading(false);
      }
    })();
  };

  // Re-validate the form, if it is in an invalid state, when any of the tracked fields change.
  useEffect(() => {
    if (! isValid) {
      validateForm();
    }
  }, [formValues, selectedLists]);

  // Fetch list groupings from the API.
  useEffect(() => {
    if (groupsLoading) {
      (async () => {
        try {
          const response =
            await fetch('/wp-json/kauffman/v1/newsletter/config');
          const data = await response.json();
          setGroups(data);
        } catch ({ message }) {
          setErrors([message]);
        } finally {
          setGroupsLoading(false);
        }
      })();
    }
  }, [groupsLoading]);

  // Display loading message if we haven't loaded the list groups yet.
  if (groupsLoading) {
    return (
      <div>Loading...</div>
    );
  }

  // Render the component.
  return (
    <CustomForm
      errors={errors}
      formValues={formValues}
      groups={groups}
      loading={loading}
      onFormSubmit={onFormSubmit}
      onInputChange={onInputChange}
      onListSelect={onListSelect}
      selectedLists={selectedLists}
      status={status}
    />
  );
};

export default Newsletters;
